import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Row, notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { getMenuId } from '../../utils/helpers';
import CreateUpdateSubcategoryForm from '../../components/CreateUpdateSubcategoryForm';
import axios from 'axios';
import store from 'store';
import { baseURL } from '../../utils/helpers';

const authToken = store.get('authenticationToken');

function SubcategoryCreate({ history }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHeaderComponent } = useContext(LayoutContext);
  const { doFetch: createSubcategoryFetch } = useFetch();
  const { doFetch: uploadSubcategoryImageFetch } = useFetch();
  const { doFetch: fetchLanguages } = useFetch();
  const { doFetch: fetchCategories } = useFetch();
  const { doFetch: fetchSubcategories } = useFetch();

  // Data states
  const [languageList, setLanguageList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [subcategoryList, setSubcategoryList] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Search states
  const [searchCategories, setSearchCategories] = useState([]);
  const [isSearchingCategories, setIsSearchingCategories] = useState(false);

  // Load all required data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        const menuId = getMenuId();

        if (!menuId) {
          console.error('Menu ID not found');
          setIsLoading(false);
          return;
        }

        // Load all data in parallel
        const promises = [
          // Languages
          new Promise((resolve, reject) => {
            fetchLanguages({
              url: URLS.languages,
              onSuccess: resolve,
              onError: reject,
            });
          }),

          // Categories (needed for the category dropdown)
          new Promise((resolve, reject) => {
            fetchCategories({
              url: URLS.getAllCategories(menuId),
              onSuccess: resolve,
              onError: reject,
            });
          }),

          // Subcategories (might be needed for form consistency)
          new Promise((resolve, reject) => {
            fetchSubcategories({
              url: URLS.getAllSubcategories(menuId),
              onSuccess: resolve,
              onError: reject,
            });
          }),
        ];

        const [languagesData, categoriesData, subcategoriesData] = await Promise.all(promises);

        // Transform categories data to match expected format
        const transformedCategories = categoriesData.categories.map(cat => ({
          id: cat.id,
          translations: cat.category_translations.map(translation => ({
            lanId: translation.language_id,
            lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
            name: translation.name_text,
          })),
          image: cat.image_url,
          tags: [],
          productsCount: 0,
        }));

        // Transform subcategories data if available
        let transformedSubcategories = [];
        if (subcategoriesData.subcategories) {
          transformedSubcategories = subcategoriesData.subcategories.map(sub => ({
            id: sub.id,
            categoryId: sub.category_id,
            translations: sub.subcategory_translations.map(translation => ({
              lanId: translation.language_id,
              lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
              name: translation.name_text,
            })),
            image: sub.image_url,
            productsCount: 0,
          }));
          setSubcategoryList(transformedSubcategories);
        }

        setLanguageList(languagesData);
        setCategoryList(transformedCategories);
        setTagList([]); // Tags not currently used in subcategory creation

        setHeaderComponent('Create New Subcategory');

      } catch (error) {
        console.error('Error loading subcategory create data:', error);
        // Handle error - maybe show error message
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      setHeaderComponent(null);
    };
  }, [fetchLanguages, fetchCategories, fetchSubcategories]);

  const uploadSubcategoryImage = async (subcategoryData, formValues) => {
    if (!formValues.image) {
      notification.success({
        placement: 'bottomRight',
        message: 'Subcategory created successfully.',
        duration: 3,
      });
      // Invalidate subcategories cache to refetch the list with the new subcategory
      queryClient.invalidateQueries({ queryKey: ['subcategories'] });
      navigate('/subcategories');
      return;
    }

    await uploadSubcategoryImageFetch({
      method: 'FILE_POST',
      url: URLS.uploadSubcategoryImage({ subcategoryId: subcategoryData.id }),
      params: { image: formValues?.image?.file?.originFileObj },
      showSuccessNotification: true,
      successMessage: 'Subcategory created successfully.',
      onSuccess: () => {
        // Invalidate subcategories cache to refetch the list with the new subcategory
        queryClient.invalidateQueries({ queryKey: ['subcategories'] });
        navigate('/subcategories');
      },
    });
  };

  const createNewSubcategory = async formValues => {
    const translationsObject = languageList.reduce((acc, language) => {
      acc[language.id] = formValues[`name.${language.id}`];
      return acc;
    }, {});

    console.log('translationsObject: ', translationsObject);
    console.log('formValues: ', formValues);

    await createSubcategoryFetch({
      url: URLS.createSubcategory({ categoryId: formValues.categoryId }),
      params: { ...translationsObject, tag_ids: formValues.tagIds },
      onSuccess: data => uploadSubcategoryImage(data, formValues),
    });
  };

  const handleSearchCategories = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setSearchCategories([]);
      return;
    }

    setIsSearchingCategories(true);
    try {
      const response = await axios.get(
        `${baseURL}${URLS.searchCategories()}`,
        {
          params: { q: searchQuery, per_page: 20 },
          headers: { Authorization: authToken },
        }
      );

      // Transform search results to match the structure of original categoryList
      let transformedCategories = (response.data.categories || []).map(cat => ({
        id: cat.id,
        translations: cat.category_translations.map(translation => ({
          lanId: translation.language_id,
          lanName: languageList.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
          name: translation.name_text,
        })),
        image: cat.image_url,
        tags: [],
        productsCount: 0,
      }));

      setSearchCategories(transformedCategories);
    } catch (error) {
      console.error('Error searching categories:', error);
      setSearchCategories([]);
    } finally {
      setIsSearchingCategories(false);
    }
  }, [languageList]);

  if (isLoading) {
    return (
      <Row>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          Loading subcategory creation data...
        </div>
      </Row>
    );
  }

  return (
    <Row>
      <CreateUpdateSubcategoryForm
        tagList={tagList}
        categoryList={categoryList}
        languageList={languageList}
        onSubmit={createNewSubcategory}
        // Search functionality
        searchCategories={searchCategories}
        isSearchingCategories={isSearchingCategories}
        onSearchCategories={handleSearchCategories}
      />
    </Row>
  );
}

export default SubcategoryCreate;
